import axios from 'axios';
import api from '../config/axios';
import API_ROUTES from '../config/apiRoutes';

// --- Importamos los tipos necesarios ---
import { ApiResponse } from '../interfaces/api.types';
import { Audiencia, NewAudienciaFormData } from '../interfaces/audiencia.types';
import { UpdateUserFormData } from '../interfaces/user.types';

// --- (Puedes reutilizar la función handleApiError aquí o importarla desde un archivo de utilidades) ---
const handleApiError = (error: unknown): never => {
    if (axios.isAxiosError(error) && error.response) {
        throw new Error(error.response.data.message || 'Ocurrió un error en el servidor.');
    }
    throw new Error('Ocurrió un error de conexión o un problema desconocido.');
};


/**
 * @function createAudiencia
 * @description Agenda una nueva audiencia para un caso.
 * @param {NewAudienciaFormData} audienciaData - Los datos de la nueva audiencia.
 * @returns {Promise<Audiencia>} El objeto de la audiencia recién creada.
 */
export const createAudiencia = async (audienciaData: NewAudienciaFormData): Promise<Audiencia> => {
    try {
        const response = await api.post<ApiResponse<Audiencia>>(API_ROUTES.audiencias.create, audienciaData);
        if (!response.data.success || !response.data.data) {
            throw new Error(response.data.message || "No se pudo agendar la audiencia.");
        }
        return response.data.data;
    } catch (error) {
        throw handleApiError(error);
    }
};

/**
 * @function updateAudiencia
 * @description Actualiza los detalles de una audiencia existente (reprogramar, cambiar estado, etc.).
 * @param {number} audienciaId - El ID de la audiencia a actualizar.
 * @param {Partial<NewAudienciaFormData>} updateData - Los campos a modificar.
 * @returns {Promise<Audiencia>} El objeto de la audiencia con los datos actualizados.
 */
export const updateAudiencia = async (audienciaId: number, updateData: Partial<NewAudienciaFormData>): Promise<Audiencia> => {
    try {
        const response = await api.put<ApiResponse<Audiencia>>(API_ROUTES.audiencias.update(audienciaId), updateData);
        if (!response.data.success || !response.data.data) {
            throw new Error(response.data.message || "No se pudo actualizar la audiencia.");
        }
        return response.data.data;
    } catch (error) {
        throw handleApiError(error);
    }
};

/**
 * @function deleteAudiencia
 * @description Elimina una audiencia de la agenda.
 * @param {number} audienciaId - El ID de la audiencia a eliminar.
 * @returns {Promise<string>} Un mensaje de confirmación.
 */
export const deleteAudiencia = async (audienciaId: number): Promise<string> => {
    try {
        const response = await api.delete<ApiResponse<null>>(API_ROUTES.audiencias.delete(audienciaId));
        return response.data.message || 'Audiencia eliminada exitosamente.';
    } catch (error) {
        throw handleApiError(error);
    }
};